import {
  AdjustmentType,
  FulfillmentStatus,
  FulfillmentType,
  ItemDeliveryStatus,
  ItemRewardType,
  ItemRewardValueType,
  RewardLinkStatus
} from '@/cowboy/EnumTypes'
import { CatalogResponse } from '@/cowboy/catalog/object/CatalogResponse'
import { OrderInfoResponse } from '@/cowboy/order_info/object/OrderInfoResponse'
import { RewardLinkResponse } from '@/cowboy/reward_link/object/RewardLinkResponse'
import { CatalogBrandResponse } from '@/cowboy/catalog/object/CatalogBrandResponse'
import { BrandItemResponse } from '@/cowboy/catalog/object/BrandItemResponse'
import { v4 as uuidv4 } from 'uuid'
import { getRandomIntInclusive, getRandomString, mergeDeep } from './ToolBelt'
import { OrderResponse } from '@/cowboy/order/object/OrderResponse'
import { RedemptionHistoryResponse } from '@/cowboy/redemption/object/RedemptionHistoryResponse'

/**
 * This is a WIP data mocking tool
 * Final goals for this is to use it for test and as a dev tool
 * to speed up development even when offline.
 * Would like to put this in as a split behind the http client?!?
 */
export class TCMockDataBuilder {
  static get mockOrderInfo () {
    return MockOrderInfoBuilder
  }

  static get mockRewardLink () {
    return MockRewardLinkBuilder
  }

  static get mockCatalog () {
    return MockCatalogBuilder
  }

  static get mockBrand () {
    return MockBrandBuilder
  }

  static get mockItem () {
    return MockItemBuilder
  }

  static get mockOrder () {
    return MockOrderBuilder
  }

  static get mockRedemptionHistory () {
    return MockRedemptionHistoryBuilder
  }
}

export const randomBrandImage = (width, seed = getRandomString(5)) => {
  const height = Math.floor((width / 1.575) / 10) * 10
  return `https://picsum.photos/seed/${seed}/${width}/${height}`
}

const mockLineNumber = 1
const mockReferenceOrderId = 'RG200210-43'
const mockPlatformHash = 'P-mock-hash'
const mockRewardLinkUtid = 'U100001'
const mockCatalogId = 100
const mockCurrencyCode = 'USD'

class MockOrderInfoBuilder {
  #jsonObject = {
    referenceLineItemId: {
      lineNumber: mockLineNumber,
      referenceOrderId: mockReferenceOrderId,
      orderId: 100
    },
    splitInfo: {
      platformHash: mockPlatformHash,
      utid: mockRewardLinkUtid
    },
    item: {
      utid: mockRewardLinkUtid,
      fulfillmentType: FulfillmentType.DIGITAL,
      isExpirable: false,
      attributes: []
    }
  }

  /**
   * @return {MockOrderInfoBuilder}
   */
  static init () {
    return new MockOrderInfoBuilder()
  }

  /**
   * @return {MockOrderInfoBuilder}
   */
  with (override = {}) {
    mergeDeep(this.#jsonObject, override)
    return this
  }

  /**
   * @return {Object}
   */
  buildResponse () {
    return OrderInfoResponse.fromJSON(this.#jsonObject)
  }

  /**
   * @return {MockOrderInfoBuilder}
   */
  static asRewardLink () {
    return MockOrderInfoBuilder.init()
  }

  /**
   * @return {MockOrderInfoBuilder}
   */
  static asPromoLink () {
    return MockOrderInfoBuilder.init().with({
      item: {
        isExpirable: true
      }
    })
  }

  /**
   * @return {MockOrderInfoBuilder}
   */
  static asDisbursementLink () {
    return MockOrderInfoBuilder.init().with({
      item: {
        attributes: [{ name: 'IS_REDEEMABLE_FOR_CASH_ITEMS' }]
      }
    })
  }
}

class MockRewardLinkBuilder {
  #jsonObject = {
    currencyCode: mockCurrencyCode,
    currencyDisplay: {
      symbol: '$',
      precision: 2,
      thousand: ',',
      decimal: '.'
    },
    availableAmount: 100.00,
    currentAmount: 100.00,
    totalValue: 100.00,
    metadata: {
      catalogId: mockCatalogId,
      checkoutEmail: 'john-smith@tangocard.com',
      checkoutFirstName: 'John',
      checkoutLastName: 'Smith'
    },
    status: RewardLinkStatus.ACTIVE,
    customization: {},
    isRedeemed: false,
    expirationDate: undefined,
    createdAt: new Date().valueOf()
  }

  with (override = {}) {
    mergeDeep(this.#jsonObject, override)
    return this
  }

  withStartingBalance (totalValue = 100) {
    this.#jsonObject.availableAmount = totalValue
    this.#jsonObject.currentAmount = totalValue
    this.#jsonObject.totalValue = totalValue
    return this
  }

  withAvailableAmount (availableAmount = this.#jsonObject.totalValue) {
    this.#jsonObject.availableAmount = availableAmount
    return this
  }

  withName (firstName = 'John', lastName = 'Smith') {
    this.#jsonObject.metadata.checkoutFirstName = firstName
    this.#jsonObject.metadata.checkoutLastName = lastName
    this.#jsonObject.metadata.checkoutEmail = `${firstName.toLowerCase()}-${lastName.toLowerCase()}@tangocard.com`
    return this
  }

  withCustomization (override = {}) {
    this.#jsonObject.customization = override
    return this
  }

  buildResponse () {
    return RewardLinkResponse.fromJSON(this.#jsonObject)
  }

  static init () {
    return new MockRewardLinkBuilder()
  }

  static asRewardLink () {
    return MockRewardLinkBuilder.init()
  }

  static asPromoLink () {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)

    return MockRewardLinkBuilder.init().with({
      expirationDate: tomorrow,
      customization: {
        breakageRefundPercentage: 0.05,
        daysToExpireAfterIssuance: 1
      }
    })
  }

  static asDisbursementLink () {
    return MockRewardLinkBuilder.init()
  }
}

class MockCatalogBuilder {
  #jsonObject = {
    catalogID: mockCatalogId,
    name: getRandomString(),
    description: getRandomString(),
    brands: [],
    createdAt: new Date().valueOf(),
    lastUpdated: new Date().valueOf()
  }

  buildResponse () {
    return CatalogResponse.fromJSON(this.#jsonObject)
  }

  withDirectPaymentBrands (numberOfBrands = 2) {
    for (let i = 0; i < numberOfBrands; i++) {
      this.#jsonObject.brands.push(
        MockBrandBuilder.init(this.#jsonObject.brands.length + 1)
          .withDirectPaymentItems(1)
          .buildResponse()
          .toJSON()
      )
    }
    return this
  }

  withEcardVariableBrands (numberOfBrands = 2) {
    for (let i = 0; i < numberOfBrands; i++) {
      this.#jsonObject.brands.push(
        MockBrandBuilder.init(this.#jsonObject.brands.length + 1)
          .withEcardVariableItems(1)
          .buildResponse()
          .toJSON()
      )
    }
    return this
  }

  withEcardFixedBrands (numberOfBrands = 2, numberOfFixedItems) {
    for (let i = 0; i < numberOfBrands; i++) {
      this.#jsonObject.brands.push(
        MockBrandBuilder.init(this.#jsonObject.brands.length + 1)
          .withEcardFixedItems(numberOfFixedItems || getRandomIntInclusive(1, 5))
          .buildResponse()
          .toJSON()
      )
    }
    return this
  }

  withDonationBrands (numberOfBrands = 2) {
    for (let i = 0; i < numberOfBrands; i++) {
      this.#jsonObject.brands.push(
        MockBrandBuilder.init(this.#jsonObject.brands.length + 1)
          .withDonationItems(1)
          .buildResponse()
          .toJSON()
      )
    }
    return this
  }

  fromMockBrandBuilder (...mockBrandBuilder) {
    this.#jsonObject.brands.push(...mockBrandBuilder.map(builder => builder.buildResponse().toJSON()))
    return this
  }

  static init () {
    return new MockCatalogBuilder()
  }
}

class MockBrandBuilder {
  #jsonObject

  constructor (brandID) {
    if (brandID) {
      const brandImageSeed = `${brandID}-${getRandomString(5)}`
      this.#jsonObject = {
        brandID: brandID,
        name: `${brandID}-${getRandomString()}`,
        displayKey: `${brandID}-${getRandomString()}`,
        brandKey: `${brandID}-${getRandomString()}`,
        imageUrls: {
          '1200w-326ppi': randomBrandImage(1200, brandImageSeed),
          '130w-326ppi': randomBrandImage(130, brandImageSeed),
          '200w-326ppi': randomBrandImage(200, brandImageSeed),
          '278w-326ppi': randomBrandImage(278, brandImageSeed),
          '300w-326ppi': randomBrandImage(300, brandImageSeed),
          '80w-326ppi': randomBrandImage(80, brandImageSeed)
        },
        items: [],
        description: `Reward Description for ID ${brandID}`,
        alwaysShowDisclaimer: false,
        status: 'never used',
        reason: 'never used',
        rewardName: `Reward Name for ID ${brandID}`,
        displayName: `Reward Display Name for ID ${brandID}`,
        disclaimer: `Reward Disclaimer for ID ${brandID}`,
        terms: `Reward Terms for ID ${brandID}`,
        createdDate: new Date().valueOf(),
        lastUpdateDate: new Date().valueOf()
      }
    } else {
      throw new Error('Missing required brandID')
    }
  }

  with (override = {}) {
    mergeDeep(this.#jsonObject, override)
    return this
  }

  withDisplayName (brandDisplayName) {
    this.#jsonObject.displayName = brandDisplayName
  }

  withDescription (brandDescription) {
    this.#jsonObject.description = brandDescription
  }

  withDisclaimer (brandDisclaimer) {
    this.#jsonObject.disclaimer = brandDisclaimer
  }

  buildResponse () {
    return CatalogBrandResponse.fromJSON(this.#jsonObject)
  }

  withDirectPaymentItems (numberOfItems = 2) {
    for (let i = 0; i < numberOfItems; i++) {
      this.#jsonObject.items.push(
        MockItemBuilder.init(`${this.#jsonObject.brandID}${this.#jsonObject.items.length + 1}`, this.#jsonObject.brandID)
          .asDirectPaymentItem()
          .buildResponse()
          .toJSON()
      )
    }
    return this
  }

  withEcardVariableItems (numberOfItems = 1) {
    for (let i = 0; i < numberOfItems; i++) {
      this.#jsonObject.items.push(
        MockItemBuilder.init(`${this.#jsonObject.brandID}${this.#jsonObject.items.length + 1}`, this.#jsonObject.brandID)
          .asEcardVariableItem()
          .buildResponse()
          .toJSON()
      )
    }
    return this
  }

  withEcardFixedItems (numberOfItems = 2) {
    for (let i = 0; i < numberOfItems; i++) {
      this.#jsonObject.items.push(
        MockItemBuilder.init(`${this.#jsonObject.brandID}${this.#jsonObject.items.length + 1}`, this.#jsonObject.brandID)
          .asEcardFixedItem()
          .buildResponse()
          .toJSON()
      )
    }
    return this
  }

  withDonationItems (numberOfItems = 3) {
    for (let i = 0; i < numberOfItems; i++) {
      this.#jsonObject.items.push(
        MockItemBuilder.init(`${this.#jsonObject.brandID}${this.#jsonObject.items.length + 1}`, this.#jsonObject.brandID)
          .asDonationItem()
          .buildResponse()
          .toJSON()
      )
    }
    return this
  }

  fromMockItemBuilder (...mockItemBuilder) {
    this.#jsonObject.items.push(...mockItemBuilder.map(builder => builder.buildResponse().toJSON()))
    return this
  }

  static init (brandID) {
    return new MockBrandBuilder(brandID)
  }
}

/**
 * jsawyer - what are the types for the three categories?
 * Direct payment :
 *   REWARD TYPE : ONLY ACH and CARD
 *   FULFILLMENT : always digital never physical
 *   VALUE TYPE : always variable
 * Donation :
 *   REWARD TYPE : always donation
 *   FULFILLMENT : always digital never physical
 *   VALUE TYPE : always variable
 * ECard :
 *   REWARD TYPE : all but ACH, CARD, and donation
 *   FULFILLMENT : both digital or physical
 *   VALUE TYPE : can be variable or fixed
 */
class MockItemBuilder {
  #jsonObject

  constructor (itemID, brandID) {
    if (itemID && brandID) {
      this.#jsonObject = {
        itemID: itemID,
        utid: uuidv4(),
        brandID: brandID,
        sku: 'never used',
        name: `Item Name for ID ${itemID}`,
        rewardName: `Item Reward Name for ID ${itemID}`,
        status: 'never used',
        isAvailable: true, // jsawyer - when is this set what is it for?
        currencyCode: mockCurrencyCode,
        listPrice: 0, // never used
        active: true, // never used
        rewardType: {},
        unitType: 'never used',
        isWholeAmountValueRequired: false, // jsawyer - when is this set what is it for?
        exchangeRateRule: 'never used',
        createdDate: new Date().valueOf(), // never used
        lastUpdateDate: new Date().valueOf() // never used
      }
    } else if (!itemID && !brandID) {
      throw new Error('Missing required itemID and brandID')
    } else if (!itemID) {
      throw new Error('Missing required itemID')
    } else if (!brandID) {
      throw new Error('Missing required brandID')
    }
  }

  static init (itemID, brandID) {
    return new MockItemBuilder(itemID, brandID)
  }

  with (override = {}) {
    mergeDeep(this.#jsonObject, override)
    return this
  }

  withFee (adjustmentType = AdjustmentType.FLAT, feeValue = 1.1) {
    this.#jsonObject.adjustment = {
      value: feeValue,
      type: adjustmentType
    }
    return this
  }

  buildResponse () {
    return BrandItemResponse.fromJSON(this.#jsonObject)
  }

  asDirectPaymentItem (rewardType = ItemRewardType.PAYMENT_ACH, minValue = 0.01, maxValue = 2000) {
    this.#jsonObject.rewardType.description = rewardType === ItemRewardType.PAYMENT_ACH ? ItemRewardType.PAYMENT_ACH : ItemRewardType.PAYMENT_DEBIT_CARD
    this.#jsonObject.valueType = ItemRewardValueType.VARIABLE_VALUE
    this.#jsonObject.fulfillmentType = FulfillmentType.DIGITAL
    this.#jsonObject.quantityAvailable = undefined
    this.#jsonObject.minValue = minValue
    this.#jsonObject.maxValue = maxValue
    this.#jsonObject.faceValue = undefined
    return this
  }

  asEcardVariableItem (rewardType = ItemRewardType.ALL, fulfillmentType = FulfillmentType.DIGITAL, minValue = 0.01, maxValue = 2000) {
    if ([ItemRewardType.PAYMENT_ACH, ItemRewardType.PAYMENT_DEBIT_CARD, ItemRewardType.DONATION].includes(rewardType)) {
      throw new Error(`Ecards can not be of type ${rewardType}`)
    }
    this.#jsonObject.rewardType.description = rewardType
    this.#jsonObject.valueType = ItemRewardValueType.VARIABLE_VALUE
    this.#jsonObject.fulfillmentType = fulfillmentType
    this.#jsonObject.quantityAvailable = undefined
    this.#jsonObject.minValue = minValue
    this.#jsonObject.maxValue = maxValue
    this.#jsonObject.faceValue = undefined
    return this
  }

  asEcardFixedItem (rewardType = ItemRewardType.ALL, fulfillmentType = FulfillmentType.DIGITAL, faceValue = getRandomIntInclusive(1, 50), quantityAvailable = 10) {
    if ([ItemRewardType.PAYMENT_ACH, ItemRewardType.PAYMENT_DEBIT_CARD, ItemRewardType.DONATION].includes(rewardType)) {
      throw new Error(`Ecards can not be of type ${rewardType}`)
    }
    this.#jsonObject.rewardType.description = rewardType
    this.#jsonObject.valueType = ItemRewardValueType.FIXED_VALUE
    this.#jsonObject.fulfillmentType = fulfillmentType
    this.#jsonObject.quantityAvailable = quantityAvailable
    this.#jsonObject.minValue = undefined
    this.#jsonObject.maxValue = undefined
    this.#jsonObject.faceValue = faceValue
    return this
  }

  asDonationItem (minValue = 0.01, maxValue = 2000) {
    this.#jsonObject.rewardType.description = ItemRewardType.DONATION
    this.#jsonObject.valueType = ItemRewardValueType.VARIABLE_VALUE
    this.#jsonObject.fulfillmentType = FulfillmentType.DIGITAL
    this.#jsonObject.quantityAvailable = undefined
    this.#jsonObject.minValue = minValue
    this.#jsonObject.maxValue = maxValue
    this.#jsonObject.faceValue = undefined
    return this
  }
}

class MockOrderBuilder {
  #jsonObject = {
    referenceOrderID: `RL-${getRandomIntInclusive(100, 1000)}`,
    status: FulfillmentStatus.PENDING,
    orderTotal: {
      currencyCode: mockCurrencyCode,
      value: 1
    },
    lineItems: [
      {
        status: FulfillmentStatus.PENDING,
        lineNumber: 1,
        rewardName: 'Some reward',
        errorMessage: undefined,
        lineItemTotal: {
          currencyCode: mockCurrencyCode,
          value: 1
        },
        amountCharged: {
          currencyCode: mockCurrencyCode,
          exchangeRate: 0,
          value: 1,
          fee: 0,
          total: 1
        },
        fulfillmentType: FulfillmentType.DIGITAL,
        utid: uuidv4()
      }
    ]
  }

  addLineItemFromBrandItem (brandItem, amount = 1) {
    this.#jsonObject.orderTotal.value += amount
    this.#jsonObject.lineItems.push({
      status: FulfillmentStatus.PENDING,
      lineNumber: this.#jsonObject.lineItems.length + 1,
      rewardName: brandItem.rewardName,
      errorMessage: undefined,
      lineItemTotal: {
        currencyCode: mockCurrencyCode,
        value: amount
      },
      amountCharged: {
        currencyCode: mockCurrencyCode,
        exchangeRate: 0,
        value: amount,
        fee: 0,
        total: amount
      },
      fulfillmentType: FulfillmentType.DIGITAL,
      utid: brandItem.uuid
    })
    return this
  }

  setOrderComplete () {
    this.#jsonObject.status = FulfillmentStatus.COMPLETE
    this.#jsonObject.lineItems.forEach(lineItem => {
      lineItem.status = FulfillmentStatus.COMPLETE
    })
    return this
  }

  setOrderFailed () {
    this.#jsonObject.status = FulfillmentStatus.FAILED
    this.#jsonObject.lineItems.forEach(lineItem => {
      lineItem.status = FulfillmentStatus.FAILED
    })
    return this
  }

  clearLineItems () {
    this.#jsonObject.orderTotal.value = 0
    this.#jsonObject.lineItems = []
    return this
  }

  static fromBrandItem (brandItem, amount = 1) {
    return MockOrderBuilder.init()
      .clearLineItems()
      .addLineItemFromBrandItem(brandItem, amount)
  }

  buildResponse () {
    return OrderResponse.fromJSON(this.#jsonObject)
  }

  static init () {
    return new MockOrderBuilder()
  }
}

class MockRedemptionHistoryBuilder {
  #redemptions = []

  addFromOrder (orderResponse) {
    console.log(orderResponse)
    this.#redemptions.push(...orderResponse.lineItems.map(lineItem => ({
      referenceLineItemId: `${orderResponse.referenceOrderID}-${lineItem.lineNumber}`,
      orderTotal: orderResponse.orderTotal.value,
      quantity: 1,
      brandName: lineItem.rewardName,
      lineItemSubtotal: lineItem.amountCharged.value,
      lineItemFees: lineItem.amountCharged.fee,
      lineItemTotal: lineItem.amountCharged.total,
      currencyCode: lineItem.amountCharged.currencyCode,
      status: FulfillmentStatus.COMPLETE,
      createdAt: Date.now(),
      imageUrl: randomBrandImage(300, getRandomString(5)),
      fulfillmentType: lineItem.fulfillmentType,
      rewardType: ItemRewardType.ALL,
      directPaymentMetadata: undefined,
      obfuscatedDeliveryEmailAddress: 'joh*******@tangocard.com',
      deliveryStatus: ItemDeliveryStatus.DELIVERED,
      deliveryDate: Date.now()
    })))
    return this
  }

  static fromRandomOrder (numberOfOrders = getRandomIntInclusive(1, 2), numberOfItemsPerOrder = getRandomIntInclusive(1, 3)) {
    const redemptionHistoryBuilder = new MockRedemptionHistoryBuilder()
    Array(numberOfOrders).join().split(',').forEach((_, orderIndex) => {
      const orderBuilder = MockOrderBuilder.init().clearLineItems()
      Array(numberOfItemsPerOrder).join().split(',').forEach((_, itemIndex) => {
        orderBuilder.addLineItemFromBrandItem(
          MockItemBuilder.init(getRandomIntInclusive(1, 1000), Number.parseInt(`${orderIndex}${itemIndex}`)).buildResponse().toJSON(),
          getRandomIntInclusive(1, 3)
        )
      })
      redemptionHistoryBuilder.addFromOrder(orderBuilder.buildResponse().toJSON())
    })
    return redemptionHistoryBuilder
  }

  static fromOrderResponse (orderResponse) {
    return new MockRedemptionHistoryBuilder()
      .addFromOrder(orderResponse)
  }

  buildResponse () {
    return this.#redemptions.map(redemptionItem => RedemptionHistoryResponse.fromJSON(redemptionItem))
  }

  static init () {
    return new MockRedemptionHistoryBuilder()
  }
}
